// src/components/certificacion/CertificacionPaso.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Chip, IconButton, Button, CircularProgress,
  Collapse, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, Alert, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  CheckCircleOutlined,
  RadioButtonUncheckedOutlined,
  WarningAmberOutlined,
  ErrorOutlined,
  BlockOutlined,
  OpenInNewOutlined,
  ExpandMoreOutlined,
  ExpandLessOutlined,
  UploadFileOutlined,
  AttachFileOutlined,
  DownloadOutlined,
  DeleteOutlineOutlined,
  CloseOutlined,
  ReplayOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { certificacionService } from '../../services/certificacionService';
import SimulacionForm from './SimulacionForm';
import { apiClient } from '../../services/api';
import SimplePdfViewer from '../rcv/SimplePdfViewer';

const ESTADO_ICONO = {
  pendiente:   <RadioButtonUncheckedOutlined sx={{ color: 'text.disabled' }} />,
  en_proceso:  <CircularProgress size={20} />,
  completado:  <CheckCircleOutlined sx={{ color: 'success.main' }} />,
  con_reparo:  <WarningAmberOutlined sx={{ color: 'warning.main' }} />,
  bloqueado:   <ErrorOutlined sx={{ color: 'error.main' }} />,
  no_aplica:   <BlockOutlined sx={{ color: 'text.disabled' }} />,
};

const TIPO_CHIP = {
  manual:     { label: 'Manual',  color: 'default' },
  automatico: { label: 'Auto',    color: 'primary' },
  semiauto:   { label: 'Semi',    color: 'warning' },
};

export default function CertificacionPaso({ paso, rutEmpresa, onActualizado, pasoResultado, pasoSet }) {
  const [loading, setLoading] = useState(false);
  const [expandido, setExpandido] = useState(false);
  const [dialogNotas, setDialogNotas] = useState(false);
  const [notas, setNotas] = useState('');
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState(null);
  const [ejecutarError, setEjecutarError] = useState(null);
  const adjuntoInputRef = useRef(null);
  const [adjuntos, setAdjuntos] = useState([]);
  const [loadingAdjuntos, setLoadingAdjuntos] = useState(false);
  const [mostrarAdjuntos, setMostrarAdjuntos] = useState(false);
  const [adjuntoVisor, setAdjuntoVisor] = useState(null);
  const [visorBlobUrl, setVisorBlobUrl] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [reenvioLoading, setReenvioLoading] = useState(null); // caso_num en proceso
  const [consultandoTrackid, setConsultandoTrackid] = useState(null); // trackid en consulta
  const pollingSetRef = useRef(null);
  const [simulacionEmitidos, setSimulacionEmitidos] = useState([]);

  useEffect(() => {
    if (!adjuntoVisor) {
      if (visorBlobUrl) {
        URL.revokeObjectURL(visorBlobUrl);
        setVisorBlobUrl(null);
      }
      return;
    }
    apiClient.get(`/certificacion/adjunto/${adjuntoVisor.id}/descargar`, { responseType: 'blob' })
      .then(blob => setVisorBlobUrl(URL.createObjectURL(blob)))
      .catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjuntoVisor]);

  useEffect(() => {
    certificacionService.listarAdjuntos(rutEmpresa, paso.id)
      .then(setAdjuntos)
      .catch(() => {});
  }, [rutEmpresa, paso.id]);

  useEffect(() => {
    if (paso.codigo !== 'set_basico') return;
    setLoadingPreview(true);
    certificacionService.previewPaso(rutEmpresa, paso.id)
      .then(data => setPreview(data))
      .catch(() => {})
      .finally(() => setLoadingPreview(false));
  }, [rutEmpresa, paso.id, paso.codigo]);

  useEffect(() => {
    if (paso.codigo !== 'set_simulacion' || !expandido) return;
    certificacionService.listarEmitidosSimulacion(rutEmpresa)
      .then(setSimulacionEmitidos)
      .catch(() => {});
  }, [expandido, paso.codigo, rutEmpresa]);

  useEffect(() => {
    if (loading && paso.codigo === 'set_basico') {
      pollingSetRef.current = setInterval(() => {
        onActualizado();
      }, 3000);
    }
    return () => clearInterval(pollingSetRef.current);
  }, [loading, paso.codigo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubirAdjunto = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setLoadingAdjuntos(true);
    try {
      const nuevo = await certificacionService.subirAdjunto(rutEmpresa, paso.id, archivo);
      setAdjuntos(prev => [...prev, nuevo]);
    } catch (err) {
      console.error('Error subiendo adjunto:', err);
    } finally {
      setLoadingAdjuntos(false);
      e.target.value = '';
    }
  };

  const handleDescargar = async (adj) => {
    try {
      const blob = await apiClient.get(`/certificacion/adjunto/${adj.id}/descargar`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = adj.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando adjunto:', err);
    }
  };

  const handleEliminarAdjunto = async (adjuntoId) => {
    try {
      await certificacionService.eliminarAdjunto(adjuntoId);
      setAdjuntos(prev => prev.filter(a => a.id !== adjuntoId));
    } catch (err) {
      console.error('Error eliminando adjunto:', err);
    }
  };

  const handleSeleccionarArchivo = () => {
    fileInputRef.current?.click();
  };

  const handleArchivoSeleccionado = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setLoading(true);
    setUploadError(null);
    try {
      await certificacionService.cargarSetPruebas(rutEmpresa, archivo);
      onActualizado();
    } catch (err) {
      setUploadError(err.message || 'Error al cargar el archivo');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const esResultadoPaso = paso.codigo?.startsWith('resultado_');
  // Para pasos resultado: mostrar siempre si hay pasoSet con casos enviados
  const casosDelSet = pasoSet?.ultimo_resultado?.casos || [];
  const tieneResultadoSet = esResultadoPaso && casosDelSet.length > 0;
  const tieneResultado = tieneResultadoSet || (paso.ultimo_resultado &&
    (paso.estado === 'con_reparo' || paso.estado === 'bloqueado' ||
     (esResultadoPaso && paso.estado === 'completado')));

  const handleEjecutar = async () => {
    setLoading(true);
    setEjecutarError(null);
    try {
      await certificacionService.ejecutarPaso(rutEmpresa, paso.id);
      onActualizado();
    } catch (err) {
      console.error('Error ejecutando paso:', err);
      setEjecutarError(err.message || 'Error al ejecutar el paso');
      onActualizado();
    } finally {
      setLoading(false);
    }
  };

  const handleCompletarManual = async () => {
    setLoading(true);
    try {
      await certificacionService.completarPaso(rutEmpresa, paso.id, notas || null);
      setDialogNotas(false);
      setNotas('');
      onActualizado();
    } catch (err) {
      console.error('Error completando paso:', err);
    } finally {
      setLoading(false);
    }
  };

  const casosEnviados = paso.ultimo_resultado?.casos || paso.config?.casos_resultado || [];
  const casoActual = paso.config?.caso_actual;

  // Mapa caso_num → estado SII desde el paso resultado asociado (paso 9)
  const estadosSii = {};
  const _trackidItems = pasoResultado?.ultimo_resultado
    ? (Array.isArray(pasoResultado.ultimo_resultado)
        ? pasoResultado.ultimo_resultado
        : pasoResultado.ultimo_resultado.trackids || [])
    : [];
  for (const t of _trackidItems) {
    estadosSii[String(t.caso)] = t;
  }

  const _iconoCaso = (numCaso) => {
    const enviado = casosEnviados.find(c => String(c.num) === String(numCaso));
    if (enviado) {
      return enviado.estado === 'enviado'
        ? <CheckCircleOutlined sx={{ fontSize: 14, color: 'success.main' }} />
        : <ErrorOutlined sx={{ fontSize: 14, color: 'error.main' }} />;
    }
    if (casoActual?.includes(`caso ${numCaso}`)) {
      return <CircularProgress size={12} />;
    }
    return <RadioButtonUncheckedOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />;
  };

  const _opacidadCaso = (numCaso) => {
    const enviado = casosEnviados.find(c => String(c.num) === String(numCaso));
    return enviado ? 1 : 0.5;
  };

  const renderBoton = () => {
    if (paso.estado === 'no_aplica') return null;
    if (!paso.accionable) return null;

    if (paso.codigo === 'set_simulacion') {
      return <SimulacionForm rutEmpresa={rutEmpresa} paso={paso} onActualizado={onActualizado} />;
    }

    if (paso.codigo === 'cargar_set_pruebas') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            style={{ display: 'none' }}
            onChange={handleArchivoSeleccionado}
          />
          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} /> : <UploadFileOutlined />}
            onClick={handleSeleccionarArchivo}
            disabled={loading}
          >
            Subir archivo SII (.txt)
          </Button>
        </Box>
      );
    }

    if (paso.tipo_ejecucion === 'manual') {
      return (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setDialogNotas(true)}
          disabled={loading}
        >
          Marcar completado
        </Button>
      );
    }

    return (
      <Button
        size="small"
        variant="contained"
        onClick={handleEjecutar}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : null}
      >
        {paso.tipo_ejecucion === 'semiauto' ? 'Consultar' : 'Ejecutar'}
      </Button>
    );
  };

  const chip = TIPO_CHIP[paso.tipo_ejecucion] || { label: paso.tipo_ejecucion, color: 'default' };

  const esNoAplica = paso.estado === 'no_aplica';

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      py: 1,
      px: 1,
      borderRadius: 1,
      opacity: esNoAplica ? 0.45 : 1,
      '&:hover': { bgcolor: 'action.hover' },
    }}>
      {/* Icono estado + reintentar */}
      <Box sx={{ mt: 0.3, minWidth: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        {ESTADO_ICONO[paso.estado] || ESTADO_ICONO.pendiente}
        {(paso.estado === 'bloqueado' || paso.estado === 'con_reparo') && (
          <Tooltip title="Reintentar">
            <IconButton
              size="small"
              color={paso.estado === 'bloqueado' ? 'error' : 'warning'}
              disabled={loading}
              sx={{ p: 0.2 }}
              onClick={async () => {
                setLoading(true);
                try {
                  await certificacionService.reintentarPaso(rutEmpresa, paso.id);
                  onActualizado();
                } catch (err) {
                  console.error('Error reintentando paso:', err);
                } finally {
                  setLoading(false);
                }
              }}
            >
              <ReplayOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {paso.estado === 'completado' && paso.tipo_ejecucion === 'automatico' && (
          <Tooltip title="Re-enviar desde cero (nuevos folios)">
            <IconButton
              size="small"
              color="default"
              disabled={loading}
              sx={{ p: 0.2, opacity: 0.4, '&:hover': { opacity: 1 } }}
              onClick={async () => {
                setLoading(true);
                try {
                  await certificacionService.reintentarPaso(rutEmpresa, paso.id, true);
                  onActualizado();
                } catch (err) {
                  console.error('Error reseteando paso:', err);
                } finally {
                  setLoading(false);
                }
              }}
            >
              <ReplayOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Contenido */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{
            fontWeight: paso.estado === 'completado' ? 400 : 500,
            color: paso.estado === 'bloqueado' ? 'error.main'
              : paso.estado === 'completado' ? 'text.secondary'
              : 'text.primary',
            textDecoration: paso.estado === 'completado' ? 'line-through' : 'none',
          }}>
            {paso.orden}. {paso.nombre}
          </Typography>

          {!esNoAplica && (
            <Chip
              label={chip.label}
              color={chip.color}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          )}

          {paso.config?.url && (
            <Tooltip title="Abrir en portal SII">
              <IconButton
                size="small"
                href={paso.config.url}
                target="_blank"
                rel="noopener noreferrer"
                component="a"
                sx={{ p: 0.3 }}
              >
                <OpenInNewOutlined fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {esNoAplica && (
          <Typography variant="caption" color="text.disabled" fontStyle="italic">
            No aplica para esta empresa
          </Typography>
        )}

        {paso.completado_por && (
          <Typography variant="caption" color="text.disabled">
            Completado por {paso.completado_por}
            {paso.completado_at ? ` · ${new Date(paso.completado_at).toLocaleDateString()}` : ''}
          </Typography>
        )}

        {tieneResultado && (
          <Box>
            <Button
              size="small"
              variant="text"
              sx={{ px: 0, minWidth: 0, fontSize: '0.7rem' }}
              onClick={() => setExpandido(v => !v)}
              endIcon={expandido ? <ExpandLessOutlined fontSize="inherit" /> : <ExpandMoreOutlined fontSize="inherit" />}
            >
              Ver detalle
            </Button>
            <Collapse in={expandido}>
              {esResultadoPaso && tieneResultadoSet ? (() => {
                // Mapa trackid → resultado conocido
                const urItems = paso.ultimo_resultado
                  ? (Array.isArray(paso.ultimo_resultado) ? paso.ultimo_resultado : paso.ultimo_resultado.trackids || [])
                  : [];
                const resultadoPorTrackid = {};
                for (const r of urItems) resultadoPorTrackid[String(r.trackid)] = r;

                return (
                  <Box sx={{ mt: 0.5 }}>
                    {casosDelSet.map((c, i) => {
                      const trackid = String(c.trackid || '');
                      const resultado = resultadoPorTrackid[trackid] || {};
                      const estado = resultado.estado;
                      const color = estado === 'ACEPTADO' ? 'success.main'
                        : estado === 'REPARO' ? 'warning.main'
                        : estado === 'RECHAZADO' ? 'error.main'
                        : 'text.secondary';
                      const isConsultando = consultandoTrackid === trackid;
                      const handleConsultar = async () => {
                        if (!trackid) return;
                        setConsultandoTrackid(trackid);
                        try {
                          await certificacionService.consultarTrackid(rutEmpresa, trackid, String(c.num));
                          onActualizado();
                        } catch (err) {
                          console.error('Error consultando trackid:', err);
                        } finally {
                          setConsultandoTrackid(null);
                        }
                      };
                      return (
                        <Box key={i} sx={{
                          display: 'flex', flexWrap: 'wrap', gap: 0.5,
                          alignItems: 'flex-start', py: 0.4, borderBottom: '1px solid', borderColor: 'divider',
                        }}>
                          <Typography variant="caption" sx={{ minWidth: 18, fontWeight: 600 }}>
                            {c.num}.
                          </Typography>
                          <Chip label={`T${c.tipo} F${c.folio}`} size="small" variant="outlined"
                            sx={{ fontSize: '0.6rem', height: 16 }} />
                          {estado && (
                            <Chip label={estado} size="small"
                              sx={{ fontSize: '0.6rem', height: 16, color, borderColor: color, border: '1px solid' }}
                              variant="outlined" />
                          )}
                          <Tooltip title={trackid ? `Consultar trackid ${trackid}` : 'Sin trackid'}>
                            <span>
                              <IconButton size="small" onClick={handleConsultar}
                                disabled={isConsultando || !trackid}
                                sx={{ p: 0, opacity: trackid ? 0.5 : 0.2, '&:hover': { opacity: 1 } }}>
                                {isConsultando
                                  ? <CircularProgress size={12} />
                                  : <RefreshOutlined sx={{ fontSize: 14 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {resultado.glosa && (
                            <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%', pl: 3 }}>
                              {resultado.glosa}
                            </Typography>
                          )}
                          {resultado.glosa_dte && resultado.glosa_dte !== resultado.glosa && (
                            <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%', pl: 3 }}>
                              SII: {resultado.glosa_dte}
                            </Typography>
                          )}
                          {resultado.glosa_error && (
                            <Typography variant="caption" color="error.main" sx={{ flexBasis: '100%', pl: 3 }}>
                              {resultado.err_code ? `[${resultado.err_code}] ` : ''}{resultado.glosa_error}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })() : esResultadoPaso && paso.ultimo_resultado?.trackids?.length ? (() => {
                // Pasos resultado sin casos (ej. resultado_libro_ventas): mostrar trackids directamente
                const trackidItems = paso.ultimo_resultado.trackids;
                return (
                  <Box sx={{ mt: 0.5 }}>
                    {trackidItems.map((t, i) => {
                      const color = t.estado === 'ACEPTADO' ? 'success.main'
                        : t.estado === 'REPARO' ? 'warning.main'
                        : t.estado === 'RECHAZADO' ? 'error.main'
                        : 'text.secondary';
                      return (
                        <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'flex-start', py: 0.4 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            {t.trackid}
                          </Typography>
                          {t.estado && (
                            <Chip label={t.estado} size="small" variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 16, color, borderColor: color, border: '1px solid' }} />
                          )}
                          {t.glosa && (
                            <Typography variant="caption"
                              color={t.estado === 'RECHAZADO' ? 'error.main' : 'text.secondary'}
                              sx={{ flexBasis: '100%', pl: 1 }}>
                              {t.glosa}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })() : esResultadoPaso ? null : paso.codigo === 'set_simulacion' ? (
                <Box sx={{ mt: 0.5 }}>
                  {simulacionEmitidos.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {simulacionEmitidos.map((d, idx) => (
                        <Box key={`se-${idx}`} sx={{
                          display: 'grid', gridTemplateColumns: '80px 1fr 110px 20px',
                          gap: 0.5, mb: 0.4, alignItems: 'center',
                          bgcolor: 'success.50', borderRadius: 0.5, px: 0.5,
                          border: '1px solid', borderColor: 'success.200',
                        }}>
                          <Chip label={`T${d.tipo_dte}`} size="small" color="success" variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 18, width: 'fit-content' }} />
                          <Tooltip title={`TrackID: ${d.trackid}`} placement="top">
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'success.dark' }}>
                              F{d.folio} · ${d.monto_total?.toLocaleString('es-CL')}
                            </Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary">{d.fecha_emision}</Typography>
                          <CheckCircleOutlined sx={{ fontSize: 14, color: 'success.main' }} />
                        </Box>
                      ))}
                    </Box>
                  )}
                  {paso.ultimo_resultado?.documentos && (() => {
                    const res = paso.ultimo_resultado;
                    return (
                      <>
                        <Alert severity={res.ok ? 'success' : 'warning'} sx={{ mb: 0.5, py: 0.25 }}>
                          {res.ok
                            ? `${res.documentos.filter(r => r.ok).length} documentos enviados al SII correctamente`
                            : `${res.documentos.filter(r => r.ok).length} enviados, ${res.documentos.filter(r => !r.ok).length} con error`}
                        </Alert>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ '& th': { fontSize: '0.7rem', py: 0.4, color: 'text.secondary' } }}>
                              <TableCell>Tipo</TableCell>
                              <TableCell>Folio</TableCell>
                              <TableCell>Glosa</TableCell>
                              <TableCell align="right">Total</TableCell>
                              <TableCell>TrackID / Error</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {res.documentos.map((r, i) => (
                              <TableRow key={i} sx={{ bgcolor: r.ok ? 'success.50' : 'error.50' }}>
                                <TableCell sx={{ py: 0.4 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {r.ok
                                      ? <CheckCircleOutlined sx={{ fontSize: 13, color: 'success.main' }} />
                                      : <ErrorOutlined sx={{ fontSize: 13, color: 'error.main' }} />}
                                    <Typography variant="caption">T{r.tipo_dte}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ py: 0.4 }}>
                                  <Typography variant="caption" fontFamily="monospace">{r.folio ?? '—'}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.4 }}>
                                  <Typography variant="caption">{r.glosa}</Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.4 }}>
                                  <Typography variant="caption">
                                    {r.total != null ? `$${r.total.toLocaleString('es-CL')}` : '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.4, maxWidth: 180 }}>
                                  {r.trackid && (
                                    <Typography variant="caption" fontFamily="monospace" color="success.dark">
                                      {r.trackid}
                                    </Typography>
                                  )}
                                  {r.error && (
                                    <Tooltip title={r.error} placement="top">
                                      <Typography variant="caption" color="error.main" sx={{
                                        display: 'block', overflow: 'hidden',
                                        textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170,
                                      }}>
                                        {r.error}
                                      </Typography>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    );
                  })()}
                </Box>
              ) : (
                <Box sx={{
                  bgcolor: 'grey.100', borderRadius: 1, p: 1, mt: 0.5,
                  fontSize: '0.7rem', fontFamily: 'monospace',
                  maxHeight: 200, overflow: 'auto',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                  {JSON.stringify(paso.ultimo_resultado, null, 2)}
                </Box>
              )}
            </Collapse>
          </Box>
        )}

        {/* Preview set básico — tabla de casos */}
        {paso.codigo === 'set_basico' && preview?.set_cargado && (
          <Box sx={{ mt: 1, pl: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Set {preview.numero_atencion} — {preview.casos.length} casos a enviar
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {preview.casos.map(caso => {
                const numStr = String(caso.numero_caso);
                const sii = estadosSii[numStr];
                const necesitaReenvio = sii && (sii.estado === 'REPARO' || sii.estado === 'RECHAZADO');
                const siiColor = sii?.estado === 'ACEPTADO' ? 'success.main'
                  : sii?.estado === 'REPARO' ? 'warning.main'
                  : sii?.estado === 'RECHAZADO' ? 'error.main'
                  : null;
                return (
                  <Box key={caso.numero_caso} sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    py: 0.25, opacity: _opacidadCaso(caso.numero_caso),
                  }}>
                    {_iconoCaso(caso.numero_caso)}
                    <Typography variant="caption" sx={{ minWidth: 20 }}>
                      {caso.numero_caso}.
                    </Typography>
                    <Chip
                      label={caso.tipo_nombre}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 18 }}
                    />
                    {sii && (
                      <Chip
                        label={sii.estado}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: 16, color: siiColor, borderColor: siiColor }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                      {caso.descripcion}
                    </Typography>
                    {necesitaReenvio && (
                      <Tooltip title="Re-enviar con folio nuevo">
                        <span>
                          <IconButton
                            size="small"
                            sx={{ p: 0.2 }}
                            disabled={reenvioLoading === numStr}
                            onClick={async () => {
                              setReenvioLoading(numStr);
                              try {
                                await certificacionService.reenviarCasos(rutEmpresa, [numStr]);
                                onActualizado();
                              } catch (err) {
                                console.error('Error re-enviando caso:', err);
                              } finally {
                                setReenvioLoading(null);
                              }
                            }}
                          >
                            {reenvioLoading === numStr
                              ? <CircularProgress size={12} />
                              : <ReplayOutlined sx={{ fontSize: 14, color: siiColor }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Caso actual durante ejecución */}
        {paso.codigo === 'set_basico' && loading && casoActual && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">
              {casoActual}
            </Typography>
          </Box>
        )}

        {/* Lista de adjuntos expandible */}
        <Collapse in={mostrarAdjuntos}>
          <Box sx={{ pl: 0, py: 0.5 }}>
            {adjuntos.map(adj => (
              <Box key={adj.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline', color: 'primary.main' },
                  }}
                  noWrap
                  onClick={() => setAdjuntoVisor(adj)}
                >
                  {adj.filename}
                </Typography>
                <Tooltip title="Descargar">
                  <IconButton size="small" onClick={() => handleDescargar(adj)}>
                    <DownloadOutlined fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar">
                  <IconButton size="small" onClick={() => handleEliminarAdjunto(adj.id)}>
                    <DeleteOutlineOutlined fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* Botón acción + adjuntar */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        {renderBoton()}
        {uploadError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {uploadError}
          </Typography>
        )}
        {ejecutarError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, maxWidth: 200 }}>
            {ejecutarError}
          </Typography>
        )}

        {/* Botón adjuntar — siempre visible */}
        <input
          ref={adjuntoInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={handleSubirAdjunto}
        />
        <Tooltip title="Adjuntar evidencia">
          <IconButton
            size="small"
            onClick={() => adjuntoInputRef.current?.click()}
            disabled={loadingAdjuntos}
            color={adjuntos.length > 0 ? 'primary' : 'default'}
          >
            {loadingAdjuntos
              ? <CircularProgress size={16} />
              : <AttachFileOutlined fontSize="small" />
            }
          </IconButton>
        </Tooltip>
        {adjuntos.length > 0 && (
          <Chip
            label={adjuntos.length}
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => setMostrarAdjuntos(v => !v)}
            sx={{ cursor: 'pointer', height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>

      {/* Dialog visor de adjunto */}
      <Dialog
        open={Boolean(adjuntoVisor)}
        onClose={() => setAdjuntoVisor(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
          <Typography variant="subtitle2" component="span" noWrap sx={{ flex: 1 }}>
            {adjuntoVisor?.filename}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Descargar">
              <IconButton
                size="small"
                onClick={() => adjuntoVisor && handleDescargar(adjuntoVisor)}
              >
                <DownloadOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cerrar">
              <IconButton size="small" onClick={() => setAdjuntoVisor(null)}>
                <CloseOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {adjuntoVisor?.content_type === 'application/pdf' ? (
            <SimplePdfViewer pdfUrl={visorBlobUrl || ''} />
          ) : adjuntoVisor ? (
            <Box sx={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              height: '100%', p: 2, bgcolor: '#f0f0f0',
            }}>
              {visorBlobUrl && (
                <img
                  src={visorBlobUrl}
                  alt={adjuntoVisor.filename}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              )}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Dialog notas para pasos manuales */}
      <Dialog open={dialogNotas} onClose={() => setDialogNotas(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Marcar como completado</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {paso.nombre}
          </Typography>
          <TextField
            label="Notas (opcional)"
            value={notas}
            onChange={e => setNotas(e.target.value)}
            multiline
            rows={3}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogNotas(false)} disabled={loading}>Cancelar</Button>
          <Button onClick={handleCompletarManual} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
